import * as React from 'react';
import { BookOpen, Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/forms/Button';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { Select } from '@/components/forms/Select';
import { InlineActivityForm } from '@/components/common/InlineActivityForm';
import { InlineActivityTypeForm } from '@/components/common/InlineActivityTypeForm';
import { getActivityTypes } from '@/services/activity-types.service';
import { getActivities } from '@/services/activities.service';
import { useFormValidation } from '@/hooks/useFormValidation';
import {
  mergeEventActivitiesWithInitial,
  resolveSelectedActivityIds,
  toIdLabelOptions,
} from '@/components/events/event-form-utils';
import type { CreateActivityDto } from '@/types/activity.types';
import type { CreateActivityTypeDto } from '@/types/activity-type.types';
import type {
  EventFormFieldChange,
  EventFormFieldValidation,
  EventFormState,
} from '@/types/event.types';

/** Props for {@link EventActivitySection}. */
interface Props {
  form: EventFormState;
  onFieldChange: EventFormFieldChange;
  onBlur: (field: string) => void;
  validation: EventFormFieldValidation;
  initialActivityTypeId?: string;
  initialActivity?: { id: string; label: string } | null;
}

/** Activities attached to the event. */
export function EventActivitySection({
  form,
  onFieldChange,
  onBlur,
  validation,
  initialActivityTypeId,
  initialActivity,
}: Readonly<Props>) {
  const [selectedActivityTypeId, setSelectedActivityTypeId] = React.useState<string | undefined>(
    initialActivityTypeId,
  );
  const [showActivityForm, setShowActivityForm] = React.useState(false);
  const [showActivityTypeForm, setShowActivityTypeForm] = React.useState(false);
  const [newActivityData, setNewActivityData] = React.useState<Partial<CreateActivityDto>>({});
  const [newActivityTypeData, setNewActivityTypeData] = React.useState<
    Partial<CreateActivityTypeDto>
  >({});

  React.useEffect(() => {
    if (initialActivityTypeId && !selectedActivityTypeId) {
      setSelectedActivityTypeId(initialActivityTypeId);
    }
  }, [initialActivityTypeId]);

  const { data: activityTypesData } = useQuery({
    queryKey: ['activity-types', 'all'],
    queryFn: () => getActivityTypes({ pageSize: 10 }),
  });
  const activityTypes = activityTypesData?.data || [];

  const { data: activitiesData, isLoading: isLoadingActivities } = useQuery({
    queryKey: ['activities', 'by-type', selectedActivityTypeId],
    queryFn: () =>
      getActivities({
        activityTypeIds: selectedActivityTypeId ? [selectedActivityTypeId] : undefined,
        pageSize: 10,
      }),
    enabled: !!selectedActivityTypeId,
  });
  const baseActivities = activitiesData?.data || [];
  const activities = React.useMemo(
    () => mergeEventActivitiesWithInitial(baseActivities, initialActivity, form.activityId),
    [baseActivities, initialActivity, form.activityId],
  );

  const activityValidation = useFormValidation({
    code: { required: true },
    label: { required: true },
    activityTypeId: { required: true },
  });
  const activityTypeValidation = useFormValidation({
    code: { required: true },
    label: { required: true },
  });

  const activityTypeOptions = toIdLabelOptions(
    activityTypes as Array<{ id: string; label: string }>,
    (activityType) => activityType.label,
  );
  const activityOptions = toIdLabelOptions(
    activities as Array<{ id: string; label: string }>,
    (activity) => activity.label,
  );
  const selectedActivityIds = resolveSelectedActivityIds(form);

  function handleActivityTypeChange(value: string): void {
    setSelectedActivityTypeId(value);
    onFieldChange('activityIds', []);
  }
  function handleActivityIdsChange(values: string[]): void {
    onFieldChange('activityIds', values);
  }
  function handleActivityIdsBlur(): void {
    onBlur('activityIds');
  }
  function handleOpenActivityForm(): void {
    setNewActivityData({ activityTypeId: selectedActivityTypeId });
    setShowActivityForm(true);
  }
  function handleCloseActivityForm(): void {
    setShowActivityForm(false);
  }
  function handleCloseActivityTypeForm(): void {
    setShowActivityTypeForm(false);
  }
  function handleValidateActivity(): void {
    if (!activityValidation.validateForm(newActivityData)) return;
    onFieldChange('newActivity', newActivityData as CreateActivityDto);
    setShowActivityForm(false);
  }
  function handleValidateActivityType(): void {
    if (!activityTypeValidation.validateForm(newActivityTypeData)) return;
    onFieldChange('newActivityType', newActivityTypeData as CreateActivityTypeDto);
    setShowActivityTypeForm(false);
    setShowActivityForm(false);
  }

  function renderActivitiesField(): React.ReactNode {
    if (!selectedActivityTypeId) return null;
    const activitiesPlaceholder = isLoadingActivities
      ? 'Chargement...'
      : 'Sélectionner une ou plusieurs activités';
    return (
      <div className="space-y-2">
        <MultiSelect
          label="Activités"
          placeholder={activitiesPlaceholder}
          options={activityOptions}
          value={selectedActivityIds}
          onChange={handleActivityIdsChange}
          onBlur={handleActivityIdsBlur}
          required
          disabled={isLoadingActivities}
          state={validation.getFieldState('activityIds')}
          error={validation.getFieldError('activityIds')}
          inputSize="sm"
        />
        <Button
          variant="outline"
          size="sm"
          icon={Plus}
          iconPosition="left"
          onClick={handleOpenActivityForm}
        >
          Créer une nouvelle activité
        </Button>
      </div>
    );
  }

  function renderTypeAndActivities(): React.ReactNode {
    if (showActivityForm || showActivityTypeForm) return null;
    return (
      <div className="space-y-4">
        <Select
          label="Type d'activité"
          placeholder="Sélectionner un type"
          options={activityTypeOptions}
          value={selectedActivityTypeId || ''}
          onChange={handleActivityTypeChange}
          required
          size="sm"
        />
        {renderActivitiesField()}
      </div>
    );
  }

  function renderInlineActivityType(): React.ReactNode {
    if (!showActivityTypeForm) return null;
    return (
      <InlineActivityTypeForm
        newActivityTypeData={newActivityTypeData}
        setNewActivityTypeData={setNewActivityTypeData}
        activityTypeValidation={activityTypeValidation}
        onClose={handleCloseActivityTypeForm}
        onValidate={handleValidateActivityType}
      />
    );
  }

  function renderInlineActivity(): React.ReactNode {
    if (!showActivityForm || showActivityTypeForm) return null;
    return (
      <InlineActivityForm
        newActivityData={newActivityData}
        setNewActivityData={setNewActivityData}
        showActivityTypeForm={false}
        setShowActivityTypeForm={setShowActivityTypeForm}
        activityTypeOptions={activityTypeOptions}
        activityValidation={activityValidation}
        onClose={handleCloseActivityForm}
        onValidate={handleValidateActivity}
      />
    );
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <BookOpen className="w-4 h-4" />
        Activité
      </h4>
      {renderTypeAndActivities()}
      {renderInlineActivityType()}
      {renderInlineActivity()}
    </div>
  );
}
